import type {
  CustomerIdentity,
  CustomerProduct,
  CustomerProductCatalog,
  CustomerProductStatus,
  CustomerRole,
  FirmAccount,
  ProductCode,
  WalletSummary,
} from "../types/api";

const STANDARD_PRODUCT_NAMES: Partial<Record<ProductCode, string>> = {
  NATURAL_PERSON: "个人画像查询",
  ENTERPRISE_CREDIT: "企业信用全景查询",
  ENTERPRISE_OPERATION: "企业经营信息查询",
  OWNED_VEHICLE: "名下车辆查询",
  VEHICLE_DETAIL: "指定车辆查询",
  PROPERTY: "个人不动产信息查询",
  PROPERTY_ENTERPRISE: "企业不动产信息查询",
};

export interface InstitutionWorkspaceProduct {
  productCode: ProductCode;
  displayName: string;
  status: CustomerProductStatus;
  price: number | null;
  billingMode: string | null;
}

export interface InstitutionWorkspaceContextMessage {
  type: "institution:workspace-context";
  identity: {
    organizationName: string;
    role: CustomerRole;
  };
  wallet: {
    availableBalance: number;
  };
  authorizationMaterialRequired: boolean;
  capabilities: {
    financialAnalysis: { visible: boolean };
    lostContact: { visible: boolean };
    batch: { visible: boolean };
  };
  products: InstitutionWorkspaceProduct[];
}

export type InstitutionAccountCommand =
  | { type: "institution:list-accounts" }
  | { type: "institution:create-account"; name: string; mobile: string }
  | { type: "institution:adjust-account-quota"; accountId: string; amountDelta: number }
  | { type: "institution:set-account-status"; accountId: string; status: "ACTIVE" | "DISABLED" }
  | { type: "institution:reset-account-password"; accountId: string };

export interface InstitutionAccountsContextMessage {
  type: "institution:accounts-context";
  summary: {
    total: number;
    active: number;
    unallocatedBalance: number;
  };
  accounts: Array<{
    id: string;
    name: string;
    mobile: string;
    allocatedAmount: number;
    usedAmount: number;
    availableAmount: number;
    status: "ACTIVE" | "DISABLED";
  }>;
}

export interface InstitutionAccountService {
  listAccounts: () => Promise<FirmAccount[]>;
  getWallet: () => Promise<WalletSummary>;
  createAccount: (name: string, mobile: string) => Promise<FirmAccount & { initial_password: string }>;
  adjustQuota: (accountId: string, amountDelta: number) => Promise<unknown>;
  setStatus: (accountId: string, status: "ACTIVE" | "DISABLED") => Promise<unknown>;
  resetPassword: (accountId: string) => Promise<{ initial_password: string }>;
}

export type InstitutionAccountResponse = InstitutionAccountsContextMessage | {
  type: "institution:account-credentials";
  accountId: string;
  mobile: string;
  password: string;
  reason: "CREATED" | "RESET";
};

function text(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return normalized || null;
}

export function parseInstitutionAccountCommand(value: unknown): InstitutionAccountCommand | null {
  if (!value || typeof value !== "object") return null;
  const command = value as Record<string, unknown>;
  if (command.type === "institution:list-accounts") return { type: command.type };
  const accountId = text(command.accountId);
  if (command.type === "institution:create-account") {
    const name = text(command.name);
    const mobile = text(command.mobile);
    return name && mobile && /^1\d{10}$/.test(mobile)
      ? { type: command.type, name, mobile }
      : null;
  }
  if (command.type === "institution:adjust-account-quota") {
    const amountDelta = Number(command.amountDelta);
    return accountId && Number.isFinite(amountDelta) && amountDelta !== 0
      ? { type: command.type, accountId, amountDelta }
      : null;
  }
  if (command.type === "institution:set-account-status") {
    return accountId && (command.status === "ACTIVE" || command.status === "DISABLED")
      ? { type: command.type, accountId, status: command.status }
      : null;
  }
  if (command.type === "institution:reset-account-password") {
    return accountId ? { type: command.type, accountId } : null;
  }
  return null;
}

export function buildInstitutionAccountsContext(
  accounts: FirmAccount[],
  wallet: WalletSummary,
): InstitutionAccountsContextMessage {
  return {
    type: "institution:accounts-context",
    summary: {
      total: accounts.length,
      active: accounts.filter((account) => account.status === "ACTIVE").length,
      unallocatedBalance: wallet.unallocated_balance,
    },
    accounts: accounts.map((account) => ({
      id: account.id,
      name: account.name,
      mobile: account.mobile,
      allocatedAmount: account.allocated_amount,
      usedAmount: account.used_amount,
      availableAmount: account.spending_limit,
      status: account.status,
    })),
  };
}

async function refreshedAccounts(service: InstitutionAccountService): Promise<InstitutionAccountsContextMessage> {
  const [accounts, wallet] = await Promise.all([service.listAccounts(), service.getWallet()]);
  return buildInstitutionAccountsContext(accounts, wallet);
}

export async function runInstitutionAccountCommand(
  command: InstitutionAccountCommand,
  service: InstitutionAccountService,
): Promise<InstitutionAccountResponse[]> {
  if (command.type === "institution:list-accounts") {
    return [await refreshedAccounts(service)];
  }
  if (command.type === "institution:create-account") {
    const created = await service.createAccount(command.name, command.mobile);
    return [{
      type: "institution:account-credentials",
      accountId: created.id,
      mobile: created.mobile,
      password: created.initial_password,
      reason: "CREATED",
    }, await refreshedAccounts(service)];
  }
  if (command.type === "institution:adjust-account-quota") {
    await service.adjustQuota(command.accountId, command.amountDelta);
    return [await refreshedAccounts(service)];
  }
  if (command.type === "institution:set-account-status") {
    await service.setStatus(command.accountId, command.status);
    return [await refreshedAccounts(service)];
  }
  const accounts = await service.listAccounts();
  const account = accounts.find((item) => item.id === command.accountId);
  if (!account) throw new Error("子账号不存在或已失效");
  const result = await service.resetPassword(command.accountId);
  return [{
    type: "institution:account-credentials",
    accountId: account.id,
    mobile: account.mobile,
    password: result.initial_password,
    reason: "RESET",
  }];
}

function toInstitutionProduct(product: CustomerProduct): InstitutionWorkspaceProduct | null {
  const displayName = STANDARD_PRODUCT_NAMES[product.product_code];
  if (!displayName) return null;
  return {
    productCode: product.product_code,
    displayName,
    status: product.status,
    price: product.institution_price === null ? null : Number(product.institution_price),
    billingMode: product.customer_billing_mode,
  };
}

export function buildInstitutionWorkspaceContext(
  identity: CustomerIdentity,
  wallet: WalletSummary,
  catalog: CustomerProductCatalog,
): InstitutionWorkspaceContextMessage {
  const catalogProducts = catalog.modules.flatMap((module) => module.products);
  const financialAnalysisVisible = catalog.modules.some(
    (module) => module.module_code === "FINANCIAL_ANALYSIS" && module.products.length > 0,
  );
  return {
    type: "institution:workspace-context",
    identity: {
      organizationName: identity.organization_name,
      role: identity.role,
    },
    wallet: {
      availableBalance: identity.role === "ORGANIZATION_ADMIN"
        ? wallet.firm_balance
        : wallet.lawyer_quota,
    },
    authorizationMaterialRequired: identity.authorization_material_required,
    capabilities: {
      financialAnalysis: { visible: financialAnalysisVisible },
      lostContact: {
        visible: catalogProducts.some((product) => String(product.product_code) === "LOST_CONTACT_REPAIR"),
      },
      batch: {
        visible: catalogProducts.some((product) => String(product.product_code).startsWith("BATCH_")),
      },
    },
    products: catalogProducts
      .map(toInstitutionProduct)
      .filter((product): product is InstitutionWorkspaceProduct => product !== null),
  };
}
